import React from 'react';
import { darken, makeStyles } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';

import { colors } from 'src/themes/colors';

import MHButton from '../MHButton';
import {
  AttendanceOptionLabel,
  ModalityOptionLabel,
  SessionTypeOptionLabel,
} from './InvoicingForm';

const useStyles = makeStyles({
  dialog: {
    maxWidth: 670,
  },
  submitButton: {
    width: 146,
    marginLeft: 11,
  },
  cancelButton: {
    backgroundColor: colors.white1,
    color: colors.black7,
    width: 146,
    border: `1px solid ${colors.gray16}`,
    '&:hover': {
      backgroundColor: darken(colors.white1, 0.05),
    },
  },
});

export enum ListItemTestId {
  Name = 'name',
  Company = 'company',
  Email = 'email',
  DateAndTime = 'dateAndTime',
  Attendance = 'attendance',
  Modality = 'modality',
  SessionType = 'sessionType',
}

export enum ButtonTestId {
  Close = 'closeButton',
  Submit = 'submitButton',
}

const ListItem: React.FC<{ boldText: string; text: string; testId: ListItemTestId }> = ({
  boldText,
  text,
  testId,
}) => (
  <li className="leading-6">
    <span className="font-extrabold text-gray-850">{boldText}:</span>
    <span className="font-light text-gray-800 ml-1" data-testid={testId}>
      {text}
    </span>
  </li>
);

interface Props {
  visible: boolean;
  showSessionType: boolean;
  name: string;
  company: string;
  email: string;
  dateAndTime: string;
  saving: boolean;
  modality: ModalityOptionLabel | '';
  attendance: AttendanceOptionLabel | '';
  sessionType: SessionTypeOptionLabel | '';
  onSubmit: () => void;
  onClose: () => void;
}

export const InvoiceSubmissionSummaryModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  visible,
  showSessionType,
  name,
  company,
  email,
  dateAndTime,
  modality,
  attendance,
  sessionType,
  saving,
}) => {
  const classes = useStyles();

  return (
    <Dialog
      open={visible}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      classes={{ paper: classes.dialog }}
      fullWidth
    >
      <div id="form-dialog-title" className="p-8 pb-3">
        <h3 className="font-bold text-2xl text-gray-850">Invoice submission summary</h3>
        <h5 className="font-medium text-sm text-gray-600">Are all these details correct?</h5>
      </div>
      <div className="pt-.5 px-8 pb-3">
        <p className="font-extrabold text-gray-850">Client details:</p>
        <ul className="px-4 py-3 rounded mt-2 mb-4 bg-gray-200">
          <ListItem boldText="Name" text={name} testId={ListItemTestId.Name} />
          <ListItem boldText="Company" text={company} testId={ListItemTestId.Company} />
          <ListItem boldText="Email" text={email} testId={ListItemTestId.Email} />
        </ul>

        <p className="font-extrabold text-gray-850">Session details:</p>
        <ul className="px-4 py-3 rounded mt-2 mb-4 bg-gray-200">
          <ListItem
            boldText="Session date"
            text={dateAndTime}
            testId={ListItemTestId.DateAndTime}
          />
          <ListItem boldText="Attendance" text={attendance} testId={ListItemTestId.Attendance} />
          <ListItem boldText="Session format" text={modality} testId={ListItemTestId.Modality} />

          {showSessionType ? (
            <ListItem
              boldText="Session type"
              text={sessionType}
              testId={ListItemTestId.SessionType}
            />
          ) : null}
        </ul>
      </div>

      <div className="p-0 flex justify-end items-center m-8 mt-0 mb-6">
        <MHButton
          onClick={onClose}
          className={classes.cancelButton}
          data-testid={ButtonTestId.Close}
        >
          No, back to edit
        </MHButton>
        <MHButton
          onClick={onSubmit}
          className={classes.submitButton}
          data-testid={ButtonTestId.Submit}
          loading={saving}
        >
          Submit invoice
        </MHButton>
      </div>
    </Dialog>
  );
};
